import { CellType, NUM_PHEROMONE_CHANNELS, type SwarmMap } from './types';
import { PRNG } from './prng';

function makeMap(width: number, height: number, seed: number, name: string): SwarmMap {
  const size = width * height;
  return {
    width,
    height,
    cells: new Uint8Array(size),
    food: new Uint16Array(size),
    pheromones: new Uint16Array(size * NUM_PHEROMONE_CHANNELS),
    visitCounts: new Uint32Array(size),
    nestX: Math.floor(width / 2),
    nestY: Math.floor(height / 2),
    totalFood: 0,
    seed,
    name,
  };
}

function idx(map: SwarmMap, x: number, y: number): number {
  return y * map.width + x;
}

function setCell(map: SwarmMap, x: number, y: number, type: CellType): void {
  map.cells[idx(map, x, y)] = type;
}

function addFood(map: SwarmMap, x: number, y: number, amount: number): void {
  const i = idx(map, x, y);
  if (map.cells[i] === CellType.EMPTY) {
    map.cells[i] = CellType.FOOD;
    map.food[i] = amount;
    map.totalFood += amount;
  }
}

function placeBorder(map: SwarmMap): void {
  for (let x = 0; x < map.width; x++) {
    setCell(map, x, 0, CellType.WALL);
    setCell(map, x, map.height - 1, CellType.WALL);
  }
  for (let y = 0; y < map.height; y++) {
    setCell(map, 0, y, CellType.WALL);
    setCell(map, map.width - 1, y, CellType.WALL);
  }
}

function placeNest(map: SwarmMap): void {
  const nx = map.nestX;
  const ny = map.nestY;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const x = nx + dx;
      const y = ny + dy;
      if (x >= 0 && x < map.width && y >= 0 && y < map.height) {
        setCell(map, x, y, CellType.NEST);
      }
    }
  }
}

function dist(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}

function inBounds(map: SwarmMap, x: number, y: number): boolean {
  return x > 0 && x < map.width - 1 && y > 0 && y < map.height - 1;
}

function randomNest(map: SwarmMap, rng: PRNG, margin?: number): void {
  const m = margin ?? Math.floor(Math.min(map.width, map.height) * 0.15);
  map.nestX = m + rng.nextInt(map.width - m * 2);
  map.nestY = m + rng.nextInt(map.height - m * 2);
}

function ensureConnectivity(map: SwarmMap): void {
  const visited = new Uint8Array(map.width * map.height);
  const q: number[] = [map.nestX, map.nestY];
  visited[map.nestY * map.width + map.nestX] = 1;
  let head = 0;
  while (head < q.length) {
    const x = q[head++], y = q[head++];
    for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || nx >= map.width || ny < 0 || ny >= map.height) continue;
      const ni = ny * map.width + nx;
      if (visited[ni] || map.cells[ni] === CellType.WALL) continue;
      visited[ni] = 1;
      q.push(nx, ny);
    }
  }
  for (let i = 0; i < map.width * map.height; i++) {
    if (map.cells[i] === CellType.FOOD && !visited[i]) {
      const fy = Math.floor(i / map.width);
      const fx = i % map.width;
      // BFS from this food cell to find nearest reachable cell, removing walls along the way
      const vis2 = new Uint8Array(map.width * map.height);
      const parent = new Int32Array(map.width * map.height).fill(-1);
      const q2: number[] = [fx, fy];
      vis2[i] = 1;
      let found = -1;
      let h2 = 0;
      while (h2 < q2.length && found < 0) {
        const cx = q2[h2++], cy = q2[h2++];
        for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
          const nx = cx + dx, ny = cy + dy;
          if (nx < 1 || nx >= map.width - 1 || ny < 1 || ny >= map.height - 1) continue;
          const ni = ny * map.width + nx;
          if (vis2[ni]) continue;
          vis2[ni] = 1;
          parent[ni] = cy * map.width + cx;
          if (visited[ni]) { found = ni; break; }
          q2.push(nx, ny);
        }
      }
      if (found >= 0) {
        let cur = found;
        while (cur >= 0) {
          if (map.cells[cur] === CellType.WALL) {
            map.cells[cur] = CellType.EMPTY;
          }
          visited[cur] = 1;
          cur = parent[cur];
        }
      }
    }
  }
}

function randomWalkWalls(map: SwarmMap, rng: PRNG, count: number, stepRange: [number, number]): void {
  for (let w = 0; w < count; w++) {
    let x = 4 + rng.nextInt(map.width - 8);
    let y = 4 + rng.nextInt(map.height - 8);
    if (dist(x, y, map.nestX, map.nestY) < 8) continue;
    const steps = stepRange[0] + rng.nextInt(stepRange[1] - stepRange[0]);
    let angle = rng.next() * Math.PI * 2;
    let gapCounter = 0;
    let inGap = false;
    for (let s = 0; s < steps; s++) {
      angle += (rng.next() - 0.5) * 1.0;
      x += Math.round(Math.cos(angle));
      y += Math.round(Math.sin(angle));
      if (!inBounds(map, x, y)) break;
      if (dist(x, y, map.nestX, map.nestY) < 5) continue;
      // Periodic gaps in wall strokes
      gapCounter++;
      if (!inGap && gapCounter > 8 + rng.nextInt(12)) {
        inGap = true;
        gapCounter = 0;
      } else if (inGap && gapCounter > 2 + rng.nextInt(3)) {
        inGap = false;
        gapCounter = 0;
      }
      if (!inGap) setCell(map, x, y, CellType.WALL);
    }
  }
}

// 1. Open — random food clusters, no internal walls. Baseline foraging.
export function generateOpen(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `open-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  placeBorder(map);
  randomNest(map, rng);
  placeNest(map);

  const numClusters = 5 + rng.nextInt(4);
  for (let c = 0; c < numClusters; c++) {
    const cx = 4 + rng.nextInt(width - 8);
    const cy = 4 + rng.nextInt(height - 8);
    if (dist(cx, cy, map.nestX, map.nestY) < 15 * s) continue;
    const radius = 3 + rng.nextInt(3);
    const foodPerCell = 2 + rng.nextInt(3);
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dy * dy > radius * radius) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (inBounds(map, x, y)) {
          addFood(map, x, y, foodPerCell);
        }
      }
    }
  }

  return map;
}

// 2. Maze — wide-corridor maze using 4-cell grid spacing.
export function generateMaze(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `maze-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  placeBorder(map);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      setCell(map, x, y, CellType.WALL);
    }
  }

  // Use 4-cell grid spacing: 2-wide passages separated by 2-wide walls
  const step = 4;
  const cellW = Math.floor((width - 2) / step);
  const cellH = Math.floor((height - 2) / step);
  const visited = new Uint8Array(cellW * cellH);
  const stack: number[] = [];

  function cellToMap(cx: number, cy: number): [number, number] {
    return [1 + cx * step + 1, 1 + cy * step + 1];
  }

  function carveCell(cx: number, cy: number) {
    const [mx, my] = cellToMap(cx, cy);
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 2; dx++) {
        if (mx + dx < width - 1 && my + dy < height - 1) {
          setCell(map, mx + dx, my + dy, CellType.EMPTY);
        }
      }
    }
    visited[cy * cellW + cx] = 1;
  }

  function carvePassage(cx1: number, cy1: number, cx2: number, cy2: number) {
    const [mx1, my1] = cellToMap(cx1, cy1);
    const [mx2, my2] = cellToMap(cx2, cy2);
    const minX = Math.min(mx1, mx2);
    const minY = Math.min(my1, my2);
    const maxX = Math.max(mx1, mx2) + 1;
    const maxY = Math.max(my1, my2) + 1;
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (x > 0 && x < width - 1 && y > 0 && y < height - 1) {
          setCell(map, x, y, CellType.EMPTY);
        }
      }
    }
  }

  const startCX = Math.floor(cellW / 2);
  const startCY = Math.floor(cellH / 2);
  carveCell(startCX, startCY);
  stack.push(startCX, startCY);

  const dirs = [[0, -1], [1, 0], [0, 1], [-1, 0]];

  while (stack.length > 0) {
    const cy = stack[stack.length - 1];
    const cx = stack[stack.length - 2];
    const unvisited: number[] = [];
    for (let d = 0; d < 4; d++) {
      const nx = cx + dirs[d][0];
      const ny = cy + dirs[d][1];
      if (nx >= 0 && nx < cellW && ny >= 0 && ny < cellH && !visited[ny * cellW + nx]) {
        unvisited.push(d);
      }
    }
    if (unvisited.length === 0) {
      stack.length -= 2;
      continue;
    }
    const d = unvisited[rng.nextInt(unvisited.length)];
    const nx = cx + dirs[d][0];
    const ny = cy + dirs[d][1];
    carvePassage(cx, cy, nx, ny);
    carveCell(nx, ny);
    stack.push(nx, ny);
  }

  // Knock out wall segments between adjacent passages to create loops
  for (let cy = 0; cy < cellH; cy++) {
    for (let cx = 0; cx < cellW; cx++) {
      if (!visited[cy * cellW + cx]) continue;
      for (const [dx, dy] of [[1, 0], [0, 1]]) {
        const nx = cx + dx, ny = cy + dy;
        if (nx >= cellW || ny >= cellH || !visited[ny * cellW + nx]) continue;
        if (rng.next() < 0.25) {
          carvePassage(cx, cy, nx, ny);
        }
      }
    }
  }

  // Place nest on a random empty cell in the maze
  for (let attempt = 0; attempt < 100; attempt++) {
    const nx = 4 + rng.nextInt(width - 8);
    const ny = 4 + rng.nextInt(height - 8);
    if (map.cells[idx(map, nx, ny)] === CellType.EMPTY) {
      map.nestX = nx;
      map.nestY = ny;
      break;
    }
  }
  placeNest(map);

  // Food in small clusters scattered throughout
  const numClusters = 16 + rng.nextInt(7);
  for (let c = 0; c < numClusters; c++) {
    for (let attempt = 0; attempt < 50; attempt++) {
      const x = 2 + rng.nextInt(width - 4);
      const y = 2 + rng.nextInt(height - 4);
      if (map.cells[idx(map, x, y)] === CellType.EMPTY && dist(x, y, map.nestX, map.nestY) > 12 * s) {
        const r = 1 + rng.nextInt(3);
        const foodPerCell = 3 + rng.nextInt(6);
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            if (dx * dx + dy * dy > r * r) continue;
            if (inBounds(map, x + dx, y + dy) && map.cells[idx(map, x + dx, y + dy)] === CellType.EMPTY) {
              addFood(map, x + dx, y + dy, foodPerCell);
            }
          }
        }
        break;
      }
    }
  }

  ensureConnectivity(map);
  return map;
}

// 3. Spiral — concentric wavy ring walls with wide random gaps.
export function generateSpiral(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `spiral-${seed}`);
  const rng = new PRNG(seed);

  placeBorder(map);
  placeNest(map);

  const cx = width / 2;
  const cy = height / 2;
  const maxR = Math.min(width, height) / 2 - 2;
  const ringSpacing = Math.max(5, Math.floor(width * 0.06));

  for (let r = ringSpacing + 2; r < maxR; r += ringSpacing) {
    const gapAngle = rng.next() * Math.PI * 2;
    const gapSize = 0.6 + rng.next() * 0.4;
    const waveFreq = 2 + rng.nextInt(3);
    const waveAmp = 1.0 + rng.next() * 1.5;
    const wavePhase = rng.next() * Math.PI * 2;

    for (let a = 0; a < Math.PI * 2; a += 0.02) {
      const angleDist = Math.abs(((a - gapAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (angleDist < gapSize) continue;
      // Taper wave amplitude to zero near gap edges so waves can't seal gaps
      const gapFade = Math.min(1.0, (angleDist - gapSize) / 0.5);
      const amp = waveAmp * gapFade;
      const wavyR = r + Math.sin(a * waveFreq + wavePhase) * amp;
      const x = Math.floor(cx + Math.cos(a) * wavyR);
      const y = Math.floor(cy + Math.sin(a) * wavyR);
      if (inBounds(map, x, y)) {
        setCell(map, x, y, CellType.WALL);
      }
    }
  }

  // Food clusters between rings
  for (let r = ringSpacing; r < maxR; r += ringSpacing) {
    const midR = r + ringSpacing / 2;
    const numClusters = 3 + rng.nextInt(3);
    for (let c = 0; c < numClusters; c++) {
      const a = rng.next() * Math.PI * 2;
      const fr = midR + (rng.next() - 0.5) * (ringSpacing * 0.3);
      const fcx = Math.floor(cx + Math.cos(a) * fr);
      const fcy = Math.floor(cy + Math.sin(a) * fr);
      const clusterR = 1 + rng.nextInt(2);
      const foodPerCell = 3 + rng.nextInt(3);
      for (let dy = -clusterR; dy <= clusterR; dy++) {
        for (let dx = -clusterR; dx <= clusterR; dx++) {
          if (dx * dx + dy * dy > clusterR * clusterR) continue;
          const x = fcx + dx;
          const y = fcy + dy;
          if (inBounds(map, x, y)) {
            addFood(map, x, y, foodPerCell);
          }
        }
      }
    }
  }

  ensureConnectivity(map);
  return map;
}

// 4. Field — open with a few lazy curvy walls. Easy, approachable.
export function generateField(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `field-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  placeBorder(map);
  randomNest(map, rng);
  placeNest(map);

  // Long sweeping curvy wall strokes
  randomWalkWalls(map, rng, 3 + rng.nextInt(3), [70, 130]);

  // Random food clusters
  const numClusters = 6 + rng.nextInt(3);
  for (let c = 0; c < numClusters; c++) {
    const fx = 4 + rng.nextInt(width - 8);
    const fy = 4 + rng.nextInt(height - 8);
    if (dist(fx, fy, map.nestX, map.nestY) < 12 * s) continue;
    const radius = 2 + rng.nextInt(4);
    const foodPerCell = 2 + rng.nextInt(4);
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dy * dy > radius * radius) continue;
        const x = fx + dx;
        const y = fy + dy;
        if (inBounds(map, x, y)) {
          addFood(map, x, y, foodPerCell);
        }
      }
    }
  }

  ensureConnectivity(map);
  return map;
}

// 5. Bridge — vertical wall dividing map, 2-4 narrow crossings.
export function generateBridge(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `bridge-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  placeBorder(map);

  const wallCX = Math.floor(width / 2);
  const maxDrift = Math.floor(width * 0.1);

  // Organic river-like wall: lazy random walk that drifts left/right
  const waveX = new Int32Array(height);
  let wx = wallCX;
  for (let y = 1; y < height - 1; y++) {
    if (rng.next() < 0.3) wx += rng.nextInt(3) - 1;
    wx = Math.max(wallCX - maxDrift, Math.min(wallCX + maxDrift, wx));
    waveX[y] = wx;
    for (let dx = -1; dx <= 1; dx++) {
      const x = wx + dx;
      if (x > 0 && x < width - 1) setCell(map, x, y, CellType.WALL);
    }
  }

  const numBridges = 2 + rng.nextInt(3);
  const bridgeSpacing = Math.floor(height / (numBridges + 1));
  for (let b = 0; b < numBridges; b++) {
    const by = bridgeSpacing * (b + 1);
    const bw = 2 + rng.nextInt(2);
    for (let dy = -bw; dy <= bw; dy++) {
      const y = by + dy;
      if (y > 0 && y < height - 1) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = waveX[y] + dx;
          if (x > 0 && x < width - 1) setCell(map, x, y, CellType.EMPTY);
        }
      }
    }
  }

  map.nestX = Math.floor(width / 4);
  map.nestY = Math.floor(height / 2);
  placeNest(map);

  const numClusters = 6 + rng.nextInt(4);
  for (let c = 0; c < numClusters; c++) {
    const fx = wallCX + 4 + rng.nextInt(Math.floor(width / 2) - 6);
    const fy = 4 + rng.nextInt(height - 8);
    const r = 2 + rng.nextInt(3);
    const foodPerCell = 3 + rng.nextInt(4);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy > r * r) continue;
        const x = fx + dx;
        const y = fy + dy;
        if (inBounds(map, x, y) && map.cells[idx(map, x, y)] !== CellType.WALL) {
          addFood(map, x, y, foodPerCell);
        }
      }
    }
  }

  applyRandomOrientation(map, rng);
  return map;
}

// 6. Gauntlet — nest far left, food far right, staggered vertical walls with gaps.
export function generateGauntlet(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `gauntlet-${seed}`);
  const rng = new PRNG(seed);

  placeBorder(map);

  map.nestX = 5;
  map.nestY = Math.floor(height / 2);
  placeNest(map);

  const numWalls = 3 + rng.nextInt(2);
  const spacing = Math.floor((width - 20) / (numWalls + 1));

  for (let w = 0; w < numWalls; w++) {
    const wallX = 12 + spacing * (w + 1);
    const gapY = w % 2 === 0
      ? 4 + rng.nextInt(Math.floor(height / 3))
      : Math.floor(height * 2 / 3) + rng.nextInt(Math.floor(height / 3) - 4);
    const gapSize = 4 + rng.nextInt(4);

    for (let y = 1; y < height - 1; y++) {
      if (Math.abs(y - gapY) < gapSize) continue;
      if (wallX > 0 && wallX < width - 1) {
        setCell(map, wallX, y, CellType.WALL);
      }
    }
  }

  // Food escalates section by section: small near first wall, largest at the end
  for (let section = 1; section <= numWalls; section++) {
    const xStart = 12 + section * spacing + 4;
    const xEnd = section < numWalls ? 12 + (section + 1) * spacing - 4 : width - 6;
    if (xEnd <= xStart) continue;
    const t = (section - 1) / Math.max(1, numWalls - 1); // 0 at first section, 1 at last
    const numPockets = 2 + rng.nextInt(2) + Math.round(t * 2);
    const r = 1 + Math.round(t * 2);
    const foodPerCell = 2 + rng.nextInt(2) + Math.round(t * 3);
    for (let p = 0; p < numPockets; p++) {
      const fx = xStart + rng.nextInt(Math.max(1, xEnd - xStart));
      const fy = 4 + rng.nextInt(height - 8);
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r * r) continue;
          const x = fx + dx;
          const y = fy + dy;
          if (inBounds(map, x, y)) {
            addFood(map, x, y, foodPerCell);
          }
        }
      }
    }
  }

  applyRandomOrientation(map, rng);
  return map;
}

// 7. Pockets — circular walled cells scattered across the map, each with a narrow entrance
//    and food inside. Ants must find and squeeze through each opening.
export function generatePockets(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `pockets-${seed}`);
  const rng = new PRNG(seed);

  placeBorder(map);
  randomNest(map, rng);
  placeNest(map);

  const pockets: { cx: number; cy: number; r: number }[] = [];
  const numPockets = 7 + rng.nextInt(4);

  for (let attempt = 0; attempt < 300 && pockets.length < numPockets; attempt++) {
    const r = 7 + rng.nextInt(6);
    const cx = r + 4 + rng.nextInt(width - r * 2 - 8);
    const cy = r + 4 + rng.nextInt(height - r * 2 - 8);
    if (dist(cx, cy, map.nestX, map.nestY) < r + 8) continue;
    let overlap = false;
    for (const p of pockets) {
      if (dist(cx, cy, p.cx, p.cy) < r + p.r + 5) { overlap = true; break; }
    }
    if (overlap) continue;
    pockets.push({ cx, cy, r });

    // Draw circular wall with a narrow gap
    const gapAngle = rng.next() * Math.PI * 2;
    const gapSize = 0.20 + rng.next() * 0.10; // ~2-3 cells wide
    for (let a = 0; a < Math.PI * 2; a += 0.025) {
      const angleDist = Math.abs(((a - gapAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (angleDist < gapSize) continue;
      const wx = Math.round(cx + Math.cos(a) * r);
      const wy = Math.round(cy + Math.sin(a) * r);
      if (inBounds(map, wx, wy)) setCell(map, wx, wy, CellType.WALL);
    }

    // Food inside
    const foodR = 3 + rng.nextInt(2);
    const fpc = 2 + rng.nextInt(3);
    for (let dy = -foodR; dy <= foodR; dy++) {
      for (let dx = -foodR; dx <= foodR; dx++) {
        if (dx * dx + dy * dy > foodR * foodR) continue;
        const x = cx + dx, y = cy + dy;
        if (inBounds(map, x, y) && map.cells[idx(map, x, y)] === CellType.EMPTY)
          addFood(map, x, y, fpc);
      }
    }
  }

  ensureConnectivity(map);
  return map;
}

// 8. Fortress — nest at corner (4,4), wavy concentric walls with gates, food inside.
export function generateFortress(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `fortress-${seed}`);
  const rng = new PRNG(seed);

  placeBorder(map);

  const cx = Math.floor(width / 2);
  const cy = Math.floor(height / 2);
  const numRings = 3 + rng.nextInt(2);
  const ringSpacing = Math.max(4, Math.floor(Math.min(width, height) / (numRings * 2 + 4)));

  map.nestX = 4;
  map.nestY = 4;
  placeNest(map);

  for (let ring = 1; ring <= numRings; ring++) {
    const r = ring * ringSpacing;
    const gateAngle = rng.next() * Math.PI * 2;
    const gateSize = 0.35 + rng.next() * 0.2;
    const waveFreq = 3 + rng.nextInt(3);
    const waveAmp = 1.5 + rng.next() * 2.0;
    const wavePhase = rng.next() * Math.PI * 2;

    for (let a = 0; a < Math.PI * 2; a += 0.015) {
      const angleDist = Math.abs(((a - gateAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (angleDist < gateSize) continue;
      const wavyR = r + Math.sin(a * waveFreq + wavePhase) * waveAmp;
      const x = Math.floor(cx + Math.cos(a) * wavyR);
      const y = Math.floor(cy + Math.sin(a) * wavyR);
      if (inBounds(map, x, y)) {
        setCell(map, x, y, CellType.WALL);
      }
    }
  }

  // Food inside the innermost ring
  const innerR = ringSpacing - 2;
  for (let dy = -innerR; dy <= innerR; dy++) {
    for (let dx = -innerR; dx <= innerR; dx++) {
      if (dx * dx + dy * dy > innerR * innerR) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (inBounds(map, x, y)) {
        addFood(map, x, y, 3 + rng.nextInt(3));
      }
    }
  }

  // Bonus food between rings
  for (let ring = 1; ring < numRings; ring++) {
    const r = ring * ringSpacing + Math.floor(ringSpacing / 2);
    const numSpots = 4 + rng.nextInt(4);
    for (let f = 0; f < numSpots; f++) {
      const a = rng.next() * Math.PI * 2;
      const x = Math.floor(cx + Math.cos(a) * r);
      const y = Math.floor(cy + Math.sin(a) * r);
      if (x > 1 && x < width - 2 && y > 1 && y < height - 2 && map.cells[idx(map, x, y)] === CellType.EMPTY) {
        addFood(map, x, y, 2 + rng.nextInt(4));
      }
    }
  }

  ensureConnectivity(map);
  return map;
}

// 9. Grid — 3x3 or 4x4 rooms separated by walls, one doorway between adjacent rooms.
export function generateIslands(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `islands-${seed}`);
  const rng = new PRNG(seed);

  // Fill with walls
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      setCell(map, x, y, CellType.WALL);
    }
  }

  const gridSize = 4;
  const wallThick = 2;
  const cellW = Math.floor((width - 2) / gridSize);
  const cellH = Math.floor((height - 2) / gridSize);

  // Carve rooms
  const rooms: { cx: number; cy: number; ri: number; ci: number }[] = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const x0 = 1 + col * cellW + wallThick;
      const y0 = 1 + row * cellH + wallThick;
      const x1 = 1 + (col + 1) * cellW - wallThick;
      const y1 = 1 + (row + 1) * cellH - wallThick;
      const rcx = Math.floor((x0 + x1) / 2);
      const rcy = Math.floor((y0 + y1) / 2);
      rooms.push({ cx: rcx, cy: rcy, ri: row, ci: col });

      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          if (inBounds(map, x, y)) setCell(map, x, y, CellType.EMPTY);
        }
      }
    }
  }

  // Cut one doorway between each pair of adjacent rooms, track entrances per room
  const roomDoors: { x: number; y: number }[][] = rooms.map(() => []);
  const doorWidth = 2 + rng.nextInt(2);
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const ri = row * gridSize + col;
      // Door to the right
      if (col < gridSize - 1) {
        const wallX = 1 + (col + 1) * cellW;
        const doorY = 1 + row * cellH + wallThick + rng.nextInt(Math.max(1, cellH - wallThick * 2 - doorWidth));
        const doorCY = doorY + Math.floor(doorWidth / 2);
        roomDoors[ri].push({ x: wallX, y: doorCY });
        roomDoors[ri + 1].push({ x: wallX, y: doorCY });
        for (let dy = 0; dy < doorWidth; dy++) {
          for (let dx = -wallThick; dx <= wallThick; dx++) {
            if (inBounds(map, wallX + dx, doorY + dy)) {
              setCell(map, wallX + dx, doorY + dy, CellType.EMPTY);
            }
          }
        }
      }
      // Door downward
      if (row < gridSize - 1) {
        const wallY = 1 + (row + 1) * cellH;
        const doorX = 1 + col * cellW + wallThick + rng.nextInt(Math.max(1, cellW - wallThick * 2 - doorWidth));
        const doorCX = doorX + Math.floor(doorWidth / 2);
        roomDoors[ri].push({ x: doorCX, y: wallY });
        roomDoors[ri + gridSize].push({ x: doorCX, y: wallY });
        for (let dx = 0; dx < doorWidth; dx++) {
          for (let dy = -wallThick; dy <= wallThick; dy++) {
            if (inBounds(map, doorX + dx, wallY + dy)) {
              setCell(map, doorX + dx, wallY + dy, CellType.EMPTY);
            }
          }
        }
      }
    }
  }

  // Nest in a random room
  const nestRoom = rooms[rng.nextInt(rooms.length)];
  map.nestX = nestRoom.cx;
  map.nestY = nestRoom.cy;
  placeNest(map);

  // Assign distinct food styles to rooms
  const roomW = cellW - wallThick * 2;
  const roomH = cellH - wallThick * 2;
  const styles = ['empty', 'blob', 'walls', 'diffuse', 'corners'] as const;
  const styleBag: typeof styles[number][] = [];
  const doorGap = doorWidth + 5;
  for (let ri = 0; ri < rooms.length; ri++) {
    const room = rooms[ri];
    if (room === nestRoom) continue;
    if (styleBag.length === 0) {
      styleBag.push(...styles);
      rng.shuffle(styleBag);
    }
    const style = styleBag.pop()!;
    const x0 = room.cx - Math.floor(roomW / 2);
    const y0 = room.cy - Math.floor(roomH / 2);
    const doors = roomDoors[ri];

    if (style === 'empty') continue;

    if (style === 'blob') {
      const r = Math.max(1, Math.floor(Math.min(roomW, roomH) / 8));
      const bx = room.cx + rng.nextInt(5) - 2;
      const by = room.cy + rng.nextInt(5) - 2;
      const fpc = 1 + rng.nextInt(2);
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r * r) continue;
          if (inBounds(map, bx + dx, by + dy)) addFood(map, bx + dx, by + dy, fpc);
        }
      }
    } else if (style === 'walls') {
      const x1 = x0 + roomW - 1;
      const y1 = y0 + roomH - 1;
      const nearDoor = (x: number, y: number) =>
        doors.some(d => Math.abs(x - d.x) + Math.abs(y - d.y) < doorGap);
      for (let x = x0; x <= x1; x++) {
        if (rng.next() < 0.5 && inBounds(map, x, y0) && !nearDoor(x, y0)) addFood(map, x, y0, 1);
        if (rng.next() < 0.5 && inBounds(map, x, y1) && !nearDoor(x, y1)) addFood(map, x, y1, 1);
      }
      for (let y = y0 + 1; y < y1; y++) {
        if (rng.next() < 0.5 && inBounds(map, x0, y) && !nearDoor(x0, y)) addFood(map, x0, y, 1);
        if (rng.next() < 0.5 && inBounds(map, x1, y) && !nearDoor(x1, y)) addFood(map, x1, y, 1);
      }
    } else if (style === 'diffuse') {
      for (let y = y0; y < y0 + roomH; y++) {
        for (let x = x0; x < x0 + roomW; x++) {
          if (rng.next() < 0.12 && inBounds(map, x, y)) addFood(map, x, y, 1);
        }
      }
    } else if (style === 'corners') {
      const cr = Math.max(1, Math.floor(Math.min(roomW, roomH) / 7));
      const offsets = [
        [x0 + cr + 1, y0 + cr + 1],
        [x0 + roomW - cr - 2, y0 + cr + 1],
        [x0 + cr + 1, y0 + roomH - cr - 2],
        [x0 + roomW - cr - 2, y0 + roomH - cr - 2],
      ];
      for (const [ox, oy] of offsets) {
        if (rng.next() < 0.3) continue;
        const fpc = 1 + rng.nextInt(3);
        for (let dy = -cr; dy <= cr; dy++) {
          for (let dx = -cr; dx <= cr; dx++) {
            if (dx * dx + dy * dy > cr * cr) continue;
            if (inBounds(map, ox + dx, oy + dy)) addFood(map, ox + dx, oy + dy, fpc);
          }
        }
      }
    }
  }

  return map;
}

// 10. Chambers — rooms carved in rock connected by narrow corridors.
export function generateChambers(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `chambers-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      setCell(map, x, y, CellType.WALL);
    }
  }

  // Place random rectangular chambers
  const chambers: { cx: number; cy: number; hw: number; hh: number }[] = [];
  const numChambers = 11 + rng.nextInt(3);

  for (let i = 0; i < numChambers; i++) {
    const hw = 4 + rng.nextInt(4);
    const hh = 4 + rng.nextInt(4);
    const cx = hw + 2 + rng.nextInt(Math.max(1, width - hw * 2 - 4));
    const cy = hh + 2 + rng.nextInt(Math.max(1, height - hh * 2 - 4));

    // Skip if overlapping existing chamber
    let overlap = false;
    for (const c of chambers) {
      if (Math.abs(cx - c.cx) < hw + c.hw + 2 && Math.abs(cy - c.cy) < hh + c.hh + 2) {
        overlap = true;
        break;
      }
    }
    if (overlap) continue;

    chambers.push({ cx, cy, hw, hh });
    for (let y = cy - hh; y <= cy + hh; y++) {
      for (let x = cx - hw; x <= cx + hw; x++) {
        if (inBounds(map, x, y)) {
          setCell(map, x, y, CellType.EMPTY);
        }
      }
    }
  }

  // Connect chambers with narrow corridors (L-shaped)
  const corridorWidth = 1;
  for (let i = 1; i < chambers.length; i++) {
    const a = chambers[i - 1];
    const b = chambers[i];
    let x = a.cx, y = a.cy;
    // Horizontal then vertical
    while (x !== b.cx) {
      for (let d = -corridorWidth; d <= corridorWidth; d++) {
        if (inBounds(map, x, y + d)) setCell(map, x, y + d, CellType.EMPTY);
      }
      x += x < b.cx ? 1 : -1;
    }
    while (y !== b.cy) {
      for (let d = -corridorWidth; d <= corridorWidth; d++) {
        if (inBounds(map, x + d, y)) setCell(map, x + d, y, CellType.EMPTY);
      }
      y += y < b.cy ? 1 : -1;
    }
  }
  // Connect last to first for a loop
  {
    const a = chambers[chambers.length - 1];
    const b = chambers[0];
    let x = a.cx, y = a.cy;
    while (x !== b.cx) {
      for (let d = -corridorWidth; d <= corridorWidth; d++) {
        if (inBounds(map, x, y + d)) setCell(map, x, y + d, CellType.EMPTY);
      }
      x += x < b.cx ? 1 : -1;
    }
    while (y !== b.cy) {
      for (let d = -corridorWidth; d <= corridorWidth; d++) {
        if (inBounds(map, x + d, y)) setCell(map, x + d, y, CellType.EMPTY);
      }
      y += y < b.cy ? 1 : -1;
    }
  }

  // Nest in the most central chamber
  const center = chambers.reduce((best, c) => {
    const d = dist(c.cx, c.cy, width / 2, height / 2);
    return d < best.d ? { c, d } : best;
  }, { c: chambers[0], d: Infinity });
  map.nestX = center.c.cx;
  map.nestY = center.c.cy;
  placeNest(map);

  // Food in all non-nest chambers
  for (const c of chambers) {
    if (c === center.c) continue;
    const foodR = Math.min(Math.min(c.hw, c.hh) - 1, 3);
    if (foodR < 1) continue;
    const foodPerCell = 3 + rng.nextInt(3);
    for (let dy = -foodR; dy <= foodR; dy++) {
      for (let dx = -foodR; dx <= foodR; dx++) {
        if (dx * dx + dy * dy > foodR * foodR) continue;
        const x = c.cx + dx;
        const y = c.cy + dy;
        if (inBounds(map, x, y)) {
          addFood(map, x, y, foodPerCell);
        }
      }
    }
  }

  return map;
}

// 11. Prairie — food everywhere at varying density, no blobs.
export function generatePrairie(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `prairie-${seed}`);
  const rng = new PRNG(seed);

  placeBorder(map);
  randomNest(map, rng);
  placeNest(map);

  // Density hotspots (invisible attractors that increase food probability)
  const numHotspots = 6 + rng.nextInt(4);
  const hotspots: { x: number; y: number; strength: number; radius: number }[] = [];
  for (let i = 0; i < numHotspots; i++) {
    hotspots.push({
      x: 4 + rng.nextInt(width - 8),
      y: 4 + rng.nextInt(height - 8),
      strength: 0.25 + rng.next() * 0.30,
      radius: 6 + rng.nextInt(10),
    });
  }

  // Scatter food with density influenced by hotspots
  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      if (dist(x, y, map.nestX, map.nestY) < 5) continue;
      let density = 0.016;
      for (const h of hotspots) {
        const d = dist(x, y, h.x, h.y);
        if (d < h.radius) {
          density += h.strength * (1 - d / h.radius);
        }
      }
      density = Math.min(density, 0.25);
      if (rng.next() < density) {
        const amount = density > 0.12 ? 1 + rng.nextInt(3) : 1 + rng.nextInt(2);
        addFood(map, x, y, amount);
      }
    }
  }

  return map;
}

// 12. Brush — dense random wall clutter, food in medium clusters throughout.
export function generateBrush(width: number, height: number, seed: number): SwarmMap {
  const map = makeMap(width, height, seed, `brush-${seed}`);
  const rng = new PRNG(seed);
  const s = width / 128;

  placeBorder(map);
  randomNest(map, rng);
  placeNest(map);

  // Dense random wall clutter (~25-30% of interior)
  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      if (dist(x, y, map.nestX, map.nestY) < 5) continue;
      if (rng.next() < 0.28) {
        setCell(map, x, y, CellType.WALL);
      }
    }
  }

  // Clear small areas around the nest to ensure ants can leave
  for (let dy = -3; dy <= 3; dy++) {
    for (let dx = -3; dx <= 3; dx++) {
      const x = map.nestX + dx, y = map.nestY + dy;
      if (inBounds(map, x, y) && map.cells[idx(map, x, y)] === CellType.WALL) {
        setCell(map, x, y, CellType.EMPTY);
      }
    }
  }

  // Food clusters scattered throughout the rubble
  const numClusters = 10 + rng.nextInt(3);
  for (let c = 0; c < numClusters; c++) {
    for (let attempt = 0; attempt < 80; attempt++) {
      const fx = 4 + rng.nextInt(width - 8);
      const fy = 4 + rng.nextInt(height - 8);
      if (dist(fx, fy, map.nestX, map.nestY) < 10 * s) continue;
      if (map.cells[idx(map, fx, fy)] !== CellType.EMPTY) continue;
      const r = 2 + rng.nextInt(3);
      const foodPerCell = 3 + rng.nextInt(3);
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r * r) continue;
          const x = fx + dx, y = fy + dy;
          if (inBounds(map, x, y)) {
            addFood(map, x, y, foodPerCell);
          }
        }
      }
      break;
    }
  }

  ensureConnectivity(map);
  return map;
}

// Randomly apply one of 8 dihedral symmetries (flip/rotate) for orientation-agnostic maps
function applyRandomOrientation(map: SwarmMap, rng: PRNG): void {
  if (map.width !== map.height) return;
  const n = map.width;
  const flipX = rng.nextInt(2) === 1;
  const flipY = rng.nextInt(2) === 1;
  const transpose = rng.nextInt(2) === 1;
  if (!flipX && !flipY && !transpose) return;
  const newCells = new Uint8Array(n * n);
  const newFood = new Uint16Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      let tx = flipX ? n - 1 - x : x;
      let ty = flipY ? n - 1 - y : y;
      if (transpose) { const tmp = tx; tx = ty; ty = tmp; }
      newCells[ty * n + tx] = map.cells[y * n + x];
      newFood[ty * n + tx] = map.food[y * n + x];
    }
  }
  map.cells.set(newCells);
  map.food.set(newFood);
  let nx = flipX ? n - 1 - map.nestX : map.nestX;
  let ny = flipY ? n - 1 - map.nestY : map.nestY;
  if (transpose) { const tmp = nx; nx = ny; ny = tmp; }
  map.nestX = nx;
  map.nestY = ny;
}

export type MapGenerator = (width: number, height: number, seed: number) => SwarmMap;

export const MAP_GENERATORS: Record<string, MapGenerator> = {
  open: generateOpen,
  maze: generateMaze,
  spiral: generateSpiral,
  field: generateField,
  bridge: generateBridge,
  gauntlet: generateGauntlet,
  pockets: generatePockets,
  fortress: generateFortress,
  islands: generateIslands,
  chambers: generateChambers,
  prairie: generatePrairie,
  brush: generateBrush,
};

// Fixed archetype ordering for eval maps (deterministic, not shuffled).
// mapIndex / EVAL_MAPS_PER_TYPE gives the archetype index.
export const EVAL_MAPS_PER_TYPE = 10;
export const EVAL_ARCHETYPE_ORDER = [
  'open', 'maze', 'spiral', 'field', 'bridge',
  'gauntlet', 'pockets', 'fortress', 'islands', 'chambers', 'prairie', 'brush',
] as const;

// Derive an independent tag for a given (baseSeed, index) slot.
// The tag IS the generation + simulation seed, and equals parseInt(name.split('-')[1], 36).
function practiceTag(baseSeed: number, index: number): number {
  const h = Math.imul(baseSeed ^ 0x45d9f3b, 0x9e3779b9) ^ Math.imul(index + 1, 0x6c62272e);
  return (Math.imul(h ^ (h >>> 16), 0x85ebca6b) & 0x0fffffff) || 1;
}

export function generatePracticeMap(
  width: number,
  height: number,
  baseSeed: number,
  index: number,
): SwarmMap {
  const orderRng = new PRNG(baseSeed ^ 0x5a5a5a5a);
  const generators = Object.entries(MAP_GENERATORS);
  orderRng.shuffle(generators);
  const [typeName, gen] = generators[index % generators.length];
  const tag = practiceTag(baseSeed, index);
  const map = gen(width, height, tag);
  map.name = `${typeName}-${tag.toString(36)}`;
  return map;
}

// Generate an eval map given a mapIndex and a pre-generated tag (= generation + sim seed).
// Reconstruct from name: typeName=name.split('-')[0], tag=parseInt(name.split('-')[1], 36).
export function generateEvalMap(
  width: number,
  height: number,
  mapIndex: number,
  tag: number,
): SwarmMap {
  const archetypeIndex = Math.floor(mapIndex / EVAL_MAPS_PER_TYPE) % EVAL_ARCHETYPE_ORDER.length;
  const typeName = EVAL_ARCHETYPE_ORDER[archetypeIndex];
  const gen = MAP_GENERATORS[typeName];
  const map = gen(width, height, tag);
  map.name = `${typeName}-${tag.toString(36)}`;
  return map;
}

export function generatePracticeMaps(
  width: number,
  height: number,
  baseSeed: number,
  count: number,
): SwarmMap[] {
  const maps: SwarmMap[] = [];
  for (let i = 0; i < count; i++) {
    maps.push(generatePracticeMap(width, height, baseSeed, i));
  }
  return maps;
}
